import { isActiveLookupItem } from '../../../lib/isActiveLookup';
import type { SelectOption } from '../Select';

export type LookupRecord = {
  id?: number | string | null;
  code?: string | null;
  name?: string | null;
  abrv?: string | null;
  sortOrder?: number | null;
  isActive?: boolean | null;
  [key: string]: unknown;
};

/** Normalize list/paginated API payloads to an array. */
export function toLookupList(raw: unknown): LookupRecord[] {
  if (Array.isArray(raw)) return raw as LookupRecord[];
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data as LookupRecord[];
    if (Array.isArray(obj.records)) return obj.records as LookupRecord[];
    if (Array.isArray(obj.items)) return obj.items as LookupRecord[];
  }
  return [];
}

export function lookupSortOrder(item: LookupRecord): number {
  const n = Number(item.sortOrder);
  return Number.isFinite(n) ? n : 999_999;
}

export function compareLookupOrder(a: LookupRecord, b: LookupRecord): number {
  const byOrder = lookupSortOrder(a) - lookupSortOrder(b);
  if (byOrder !== 0) return byOrder;
  const byAbrv = String(a.abrv || '').localeCompare(String(b.abrv || ''), undefined, {
    sensitivity: 'base',
  });
  if (byAbrv !== 0) return byAbrv;
  const byName = String(a.name || '').localeCompare(String(b.name || ''), undefined, {
    sensitivity: 'base',
  });
  if (byName !== 0) return byName;
  return String(a.id ?? '').localeCompare(String(b.id ?? ''));
}

/** Active rows only, plus any values that must remain visible (current form value). */
export function filterActiveLookups(
  items: LookupRecord[],
  keepIds?: Array<string | number | null | undefined>,
  getValue: (item: LookupRecord) => string = (item) => String(item.id ?? ''),
): LookupRecord[] {
  const keep = new Set(
    (keepIds ?? [])
      .map((id) => (id == null || id === '' ? null : String(id)))
      .filter((id): id is string => Boolean(id)),
  );
  return items.filter((item) => {
    const value = getValue(item);
    if (value && keep.has(value)) return true;
    const id = item.id == null ? '' : String(item.id);
    if (id && keep.has(id)) return true;
    return isActiveLookupItem(item);
  });
}

export function sortLookups(items: LookupRecord[]): LookupRecord[] {
  return [...items].sort(compareLookupOrder);
}

export function labelWithAbrv(item: LookupRecord): string {
  const name = String(item.name || '').trim() || String(item.code || item.id || '');
  const abrv = String(item.abrv || '').trim();
  return abrv ? `${name} (${abrv})` : name;
}

export function labelNameOnly(item: LookupRecord): string {
  return String(item.name || item.code || item.id || '').trim();
}

export function labelCodeName(item: LookupRecord): string {
  const code = String(item.code || '').trim();
  const name = String(item.name || '').trim();
  if (code && name && code !== name) return `${code} — ${name}`;
  return name || code || String(item.id || '');
}

export function labelCurrency(item: LookupRecord): string {
  const code = String(item.code || '').trim();
  const name = String(item.name || '').trim();
  const symbol = String((item as { symbol?: string | null }).symbol || '').trim();
  if (name && symbol) return `${name} (${symbol})`;
  if (name && code) return `${name} (${code})`;
  return name || code || String(item.id || '');
}

export function labelCountry(item: LookupRecord): string {
  const name = String(item.name || '').trim();
  const iso = String(
    (item as { isoCode2?: string | null }).isoCode2 || item.code || '',
  ).trim();
  return iso ? `${name} (${iso})` : name || String(item.id || '');
}

export function labelVessel(item: LookupRecord): string {
  return String(
    (item as { vesselName?: string | null }).vesselName || item.name || item.id || '',
  ).trim();
}

export function mapLookupOptions(
  items: LookupRecord[],
  getLabel: (item: LookupRecord) => string,
  getValue: (item: LookupRecord) => string = (item) => String(item.id ?? ''),
): SelectOption[] {
  const out: SelectOption[] = [];
  for (const item of items) {
    const value = getValue(item);
    if (!value) continue;
    out.push({ value, label: getLabel(item) || value });
  }
  return out;
}

export function buildLookupSelectOptions(opts: {
  items: LookupRecord[];
  getLabel: (item: LookupRecord) => string;
  getValue?: (item: LookupRecord) => string;
  keepIds?: Array<string | number | null | undefined>;
  /** Custom sort; default is sortOrder → abrv → name. */
  compare?: (a: LookupRecord, b: LookupRecord) => number;
}): SelectOption[] {
  const getValue = opts.getValue ?? ((item: LookupRecord) => String(item.id ?? ''));
  const filtered = filterActiveLookups(opts.items, opts.keepIds, getValue);
  const sorted = opts.compare
    ? [...filtered].sort(opts.compare)
    : sortLookups(filtered);
  return mapLookupOptions(sorted, opts.getLabel, getValue);
}
